import { useState, useEffect, useCallback, useRef } from 'react';
import { ClerkProvider, SignIn, SignUp, useAuth, useUser } from '@clerk/clerk-react';
import { BRAND } from './config/brand';
import { api } from './api';
import { assignCartLineId } from './lib/cart';
import Sidebar       from './components/Sidebar';
import Topbar        from './components/Topbar';
import Dashboard     from './pages/Dashboard';
import SearchPage    from './pages/SearchPage';
import BundleBuilder from './pages/BundleBuilder';
import Presentations from './pages/Presentations';
import QuotesPage    from './pages/QuotesPage';
import StorefrontPage from './pages/StorefrontPage';
import SettingsPage  from './pages/SettingsPage';
import OnboardingPage from './pages/OnboardingPage';
import WelcomeModal  from './components/WelcomeModal';
import PublicStorefront from './pages/PublicStorefront';
import PublicPresentation from './pages/PublicPresentation';
import POList         from './pages/POList';
import PODetail       from './pages/PODetail';
import TeamPage       from './pages/TeamPage';
import AcceptInvite   from './pages/AcceptInvite';
import ClientsListPage from './pages/ClientsListPage';
import ClientDetailPage from './pages/ClientDetailPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import DecoratorsPage from './pages/DecoratorsPage';
import InvoicesPage from './pages/InvoicesPage';
import InvoicesDetailPage from './pages/InvoicesDetailPage';

const CLERK_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// Hash-based routing. Parses `#screen` and `#screen/subroute` patterns —
// first segment is the screen, anything after the slash is the subroute
// (used for detail pages like /pos/abc-123 → screen='pos', subroute='abc-123').
function useScreen() {
  const parseHash = () => window.location.hash.slice(1) || 'dashboard';
  const [hash, setHash] = useState(parseHash());
  const go = useCallback((s) => { setHash(s); window.location.hash = s; }, []);
  useEffect(() => {
    const h = () => setHash(parseHash());
    window.addEventListener('hashchange', h);
    return () => window.removeEventListener('hashchange', h);
  }, []);
  const [screen, ...subParts] = hash.split('/');
  const subroute = subParts.join('/');
  return [screen, go, subroute];
}

// ─── Main authenticated shell ─────────────────────────────────────────────────
function AppShell() {
  const { getToken } = useAuth();
  const { user }     = useUser();
  const [screen, go, subroute] = useScreen();
  const [dist, setDist]   = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart]   = useState([]); // products staged for a presentation
  const [toast, setToast] = useState(null);

  // Unsaved-changes guard for the Presentations page. Presentations.jsx
  // reports its own dirty state and save function up through these refs —
  // App.jsx owns navigation (Sidebar/Topbar both funnel through `go`), so the
  // interception has to happen here, not inside the page itself. Refs, not
  // state, because dirtiness changes on every keystroke and shouldn't
  // re-render the whole shell.
  const presDirtyRef = useRef(false);
  const presSaveRef  = useRef(null);
  const [pendingNav, setPendingNav] = useState(null);   // hash target awaiting confirmation
  const [leavingBusy, setLeavingBusy] = useState(false); // saving in flight from the leave modal
  const onPresDirtyChange = useCallback((v) => { presDirtyRef.current = v; }, []);
  const onPresRegisterSave = useCallback((fn) => { presSaveRef.current = fn; }, []);

  // Every in-app nav click (Sidebar, Topbar) goes through this instead of
  // `go` directly. Only intercepts when leaving the Presentations screen
  // while it's dirty — navigating TO presentations, or navigating while not
  // dirty, is a plain, unguarded jump.
  const guardedGo = useCallback((target) => {
    if (presDirtyRef.current && screen === 'presentations' && !String(target).startsWith('presentations')) {
      setPendingNav(target);
    } else {
      go(target);
    }
  }, [go, screen]);

  // Closing the tab or hitting refresh doesn't go through `go` at all, so it
  // needs its own guard. Reads the ref (not state) so this listener never
  // needs to be re-attached as dirtiness changes.
  useEffect(() => {
    const handler = (e) => {
      if (presDirtyRef.current) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const handleLeaveCancel = () => setPendingNav(null);
  const handleLeaveDiscard = () => {
    const target = pendingNav;
    presDirtyRef.current = false;
    setPendingNav(null);
    go(target);
  };
  const handleLeaveSave = async () => {
    if (!presSaveRef.current) { handleLeaveDiscard(); return; }
    setLeavingBusy(true);
    try {
      await presSaveRef.current();
      const target = pendingNav;
      presDirtyRef.current = false;
      setPendingNav(null);
      go(target);
    } catch (e) {
      showToast('Save failed: ' + (e?.message || 'unknown error'), 'error');
      // Leave the modal open so they can retry Save or choose Discard instead.
    } finally {
      setLeavingBusy(false);
    }
  };

  const showToast = useCallback((msg, type='success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Mid-session lockout. The lockout screen below keys off billingStatus, which
  // is fetched once at load — so a subscription that lapses WHILE someone is
  // working (period end rolls over, or a payment fails and the webhook fires)
  // would otherwise just produce a string of unexplained failures until they
  // happened to refresh. api.js emits this event on a 402/SUBSCRIPTION_INACTIVE
  // from any call, and we flip access off in place.
  //
  // Merging into existing state rather than replacing it keeps plan/period
  // fields around so the lockout screen can still say something specific.
  // New storefront quote requests, for the sidebar badge. Polled rather than
  // fetched once: a quote can arrive while the distributor has the app open,
  // and without a refresh they'd never know. Also re-fetched on screen change
  // so working through them updates the badge immediately.
  //
  // POSITION IS LOAD-BEARING: this must sit ABOVE the early returns further
  // down (loading -> Spinner, no dist -> PickPlan, onboarding). Hooks have to
  // run in the same order on every render; placing them after a conditional
  // return means they're skipped on the first pass and run on the second,
  // which throws "Rendered more hooks than during the previous render" and
  // blanks the entire app.
  const [newQuoteCount, setNewQuoteCount] = useState(0);
  useEffect(() => {
    if (!dist) return;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const t = await getToken();
        const r = await api.withToken(t).getNewQuoteCount();
        if (!cancelled) setNewQuoteCount(r?.count || 0);
      } catch { /* badge is non-critical — keep the last known value */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [dist, getToken, screen]);

  // Sidebar "paid invoice" notification — same polling shape as new quotes
  // above, re-fetched on screen change so opening the invoice that cleared it
  // (see server's paid_seen-on-view) updates the badge without a refresh.
  const [paidInvoiceCount, setPaidInvoiceCount] = useState(0);
  useEffect(() => {
    if (!dist) return;
    let cancelled = false;
    const fetchPaidCount = async () => {
      try {
        const t = await getToken();
        const r = await api.withToken(t).getPaidInvoiceCount();
        if (!cancelled) setPaidInvoiceCount(r?.count || 0);
      } catch { /* badge is non-critical — keep the last known value */ }
    };
    fetchPaidCount();
    const id = setInterval(fetchPaidCount, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [dist, getToken, screen]);

  useEffect(() => {
    const onInactive = (e) => {
      setBillingStatus(prev => ({
        ...(prev || {}),
        hasAccess: false,
        subscriptionStatus: e.detail?.subscriptionStatus || prev?.subscriptionStatus || 'canceled',
      }));
    };
    window.addEventListener('subscription-inactive', onInactive);
    return () => window.removeEventListener('subscription-inactive', onInactive);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const d = await api.withToken(token).getMe();
        setDist(d);
        // Best-effort billing-status load, with retries. Right after a fresh
        // Stripe checkout this call can race the SECOND webhook
        // (customer.subscription.created), or a Clerk token that hasn't
        // fully settled yet from the external redirect — both resolve
        // within a couple of seconds, so a couple of quick retries avoid
        // treating early arrival as a genuine billing outage.
        const loadBillingStatus = (attempt = 0) => {
          api.withToken(token).getBillingStatus()
            .then(setBillingStatus)
            .catch(() => {
              if (attempt < 2) { setTimeout(() => loadBillingStatus(attempt + 1), 1200); return; }
              // Fail open on ACCESS only — a billing hiccup must never lock
              // out or trap a paying customer. Deliberately omits
              // acceptedToS: a fetch failure says nothing about whether ToS
              // was actually accepted, and asserting `true` here previously
              // skipped onboarding entirely whenever this call raced the
              // checkout webhook — a brand-new signup landed on a blank
              // #onboarding hash (no screen maps to it outside the gate)
              // with "Untitled Company" stuck in the header for good, since
              // the one form that sets it never got a chance to render.
              setBillingStatus({ hasAccess: true, _degraded: true });
            });
        };
        loadBillingStatus();
        if (window.location.search.includes('checkout=success')) go('onboarding');
      } catch(e) {
        // "No account" = brand-new user who hasn't subscribed yet → show
        // PickPlan. Settings → Billing is for existing-subscriber billing
        // management, not signup.
        if (e.message.includes('No account')) go('pickplan');
      } finally { setLoading(false); }
    })();
  }, [getToken, go]);

  const addToCart = useCallback((product) => {
    // Add silently and toast — do NOT redirect. Distributors browsing search
    // results want to keep browsing after adding an item. They navigate to
    // Presentations themselves via the sidebar when ready. A product can be
    // added more than once (different colors / prints) — each add is its own
    // cart line via assignCartLineId.
    setCart(prev => [...prev, assignCartLineId(prev, product)]);
    showToast(`${product.name} added to presentation`);
  }, [showToast]);

  // Turn a quote request into a presentation: hydrate item IDs into full
  // product records, populate the cart, and navigate to the presentations page.
  // Client info is stashed in sessionStorage for the Presentations page to read.
  const buildPresentationFromQuote = useCallback(async (quote) => {
    const ids = (quote.items || []).map(i => i.id).filter(Boolean);
    if (ids.length === 0) {
      showToast('This quote has no products attached', 'error');
      return;
    }
    try {
      const token = await getToken();
      const r = await fetch('/api/products/by-ids', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids }),
      });
      if (!r.ok) throw new Error(`Server returned ${r.status}`);
      const data = await r.json();
      const products = data.results || [];
      if (products.length === 0) {
        showToast('Could not find the products from this quote', 'error');
        return;
      }
      // Annotate each hydrated product with the qty and color the client requested
      const metaById = Object.fromEntries((quote.items || []).map(i => [i.id, { qty: i.qty || null, color: i.color || null }]));
      const cartItems = products.map(p => ({
        ...p,
        _baseId:       p.id,
        qty:           metaById[p.id]?.qty   || null,
        color:         metaById[p.id]?.color || null,
      }));
      // Assign instance IDs for duplicate-product tracking
      const deduped = cartItems.map((p, idx) => assignCartLineId(cartItems.slice(0, idx), p));
      setCart(deduped);
      sessionStorage.setItem('quoteClientName', quote.name || 'Quote');
      go('presentations');
    } catch(err) {
      console.error('buildPresentationFromQuote failed:', err);
      showToast('Failed to build presentation', 'error');
    }
  }, [getToken, go, showToast]);

  if (loading) return <Spinner />;

  // No account yet — prompt for a plan.
  if (!dist) {
    return <PickPlanPage getToken={getToken} onAccountCreated={setDist} />;
  }

  // Onboarding gate (first login → accept ToS + pick brand).
  // IMPORTANT: only decide this once billingStatus has actually loaded. While
  // it's still null (initial load, or a transient fetch failure) we must NOT
  // treat that as "hasn't accepted" — doing so flashes every existing user
  // into onboarding, and strands them if the fetch failed. Wait for data,
  // then key strictly off acceptedToS === false.
  if (billingStatus && billingStatus.acceptedToS === false) {
    return <OnboardingPage dist={dist} getToken={getToken} onComplete={setBillingStatus} navigate={go} onSave={setDist} />;
  }
  if (!billingStatus) {
    return <Spinner />;
  }

  // Billing lockout gate.
  if (!billingStatus?.hasAccess) {
    return <BillingLockoutScreen billingStatus={billingStatus} dist={dist} />;
  }

  // Sidebar provides the main nav; screens render on the right.
  const props = { dist, getToken, showToast, addToCart, buildPresentationFromQuote, navigate: guardedGo, cart, setCart, cartItems: cart, onSave: setDist,
    onDirtyChange: onPresDirtyChange, onRegisterSave: onPresRegisterSave };

  return (
    <div style={{ display:'flex', height:'100vh', overflow:'hidden', background:'#fff', color:'#1F2937', fontFamily:"'DM Sans',system-ui,sans-serif" }}>
      <Sidebar screen={screen} navigate={guardedGo} cartCount={cart.length} newQuoteCount={newQuoteCount} paidInvoiceCount={paidInvoiceCount} dist={dist} />
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
        <Topbar screen={screen} user={user} dist={dist} cartCount={cart.length} navigate={guardedGo} />
        <div style={{ flex:1, overflow:'auto', background:'#F8FAFC' }}>
          {screen === 'dashboard'     && <Dashboard {...props} />}
          {screen === 'search'        && <SearchPage {...props} />}
          {screen === 'bundles'       && <BundleBuilder {...props} />}
          {screen === 'presentations' && <Presentations {...props} />}
          {screen === 'quotes'        && <QuotesPage {...props} />}
          {screen === 'storefront'    && <StorefrontPage {...props} />}
          {screen === 'invoices' && !subroute && <InvoicesPage {...props} />}
          {screen === 'invoices' && subroute  && <InvoicesDetailPage {...props} invoiceId={subroute} />}
          {screen === 'pos' && !subroute && <POList {...props} />}
          {screen === 'pos' && subroute  && <PODetail {...props} poId={subroute} />}
          {screen === 'clients' && !subroute && <ClientsListPage {...props} />}
          {screen === 'clients' && subroute  && <ClientDetailPage {...props} clientId={subroute} />}
          {screen === 'client' && subroute  && <ClientDetailPage {...props} clientId={subroute} />}
          {screen === 'decorators'    && <DecoratorsPage {...props} />}
          {screen === 'team' && <TeamPage {...props} navigate={go} />}
          {screen === 'settings' && <SettingsPage {...props} initialTab="margins" />}
          {screen === 'billing' && <SettingsPage {...props} initialTab="billing" />}
        </div>
      </div>
      {/* Built to show once, right after onboarding finishes — driven by
          dist.welcomed_at rather than any "just onboarded" flag, so it also
          naturally covers anyone who somehow never saw it. Was imported here
          but never actually rendered, so it had no effect. On a failed
          dismiss-persist, fake the timestamp locally rather than leaving
          `dist` untouched — otherwise this same render condition would
          reopen the modal immediately after the person just closed it. A
          real session refetch still shows it again if the server truly
          never recorded the dismissal, which is the accepted tradeoff
          WelcomeModal's own dismiss() comment already calls out. */}
      {!dist.welcomed_at && (
        <WelcomeModal dist={dist} getToken={getToken}
          onDismiss={(updated) => setDist(prev => updated || { ...prev, welcomed_at: new Date().toISOString() })} />
      )}
      {toast && (
        <div style={{
          position:'fixed', bottom:20, right:20, padding:'12px 20px',
          background: toast.type === 'error' ? '#EF4444' : '#10B981',
          color:'#fff', borderRadius:8, fontSize:14, fontWeight:500,
          boxShadow:'0 10px 15px rgba(0,0,0,0.1)', animation:'slideIn 0.3s ease'
        }}>
          {toast.msg}
        </div>
      )}
      {/* Unsaved-changes guard — only ever set while screen==='presentations'
          and the page reports itself dirty (see guardedGo above). */}
      {pendingNav && (
        <div onClick={e => { if (e.target === e.currentTarget && !leavingBusy) handleLeaveCancel(); }}
          style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.55)', zIndex:300,
            display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:'24px 26px', maxWidth:420, width:'100%',
            boxShadow:'0 20px 40px rgba(15,23,42,0.25)' }}>
            <div style={{ fontSize:16, fontWeight:700, color:'#0F172A', marginBottom:8 }}>Unsaved changes</div>
            <div style={{ fontSize:13, color:'#475569', lineHeight:1.6, marginBottom:20 }}>
              This presentation has changes that haven't been saved yet. Save before you leave, or continue and lose them.
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', flexWrap:'wrap' }}>
              <button onClick={handleLeaveCancel} disabled={leavingBusy}
                style={{ background:'#fff', border:'1px solid #E2E8F0', borderRadius:8, padding:'8px 16px',
                  fontSize:13, color:'#475569', cursor:leavingBusy?'default':'pointer', fontFamily:'inherit' }}>
                Keep editing
              </button>
              <button onClick={handleLeaveDiscard} disabled={leavingBusy}
                style={{ background:'#fff', border:'1px solid #FECACA', borderRadius:8, padding:'8px 16px',
                  fontSize:13, color:'#B91C1C', cursor:leavingBusy?'default':'pointer', fontFamily:'inherit', fontWeight:600 }}>
                Leave without saving
              </button>
              <button onClick={handleLeaveSave} disabled={leavingBusy}
                style={{ background:BRAND.colors.primary, border:'none', borderRadius:8, padding:'8px 18px',
                  fontSize:13, color:'#fff', cursor:leavingBusy?'default':'pointer', fontFamily:'inherit', fontWeight:600 }}>
                {leavingBusy ? 'Saving…' : 'Save & leave'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#F8FAFC' }}>
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:12 }}>
        <div style={{ width:40, height:40, border:`3px solid ${BRAND.colors.primary}33`, borderRadius:'50%', borderTop:`3px solid ${BRAND.colors.primary}`, animation:'spin 1s linear infinite' }} />
        <div style={{ fontSize:14, color:'#64748B' }}>Loading…</div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function BillingLockoutScreen({ billingStatus, dist }) {
  const plan = billingStatus?.plan?.toUpperCase() || 'STARTER';
  const status = billingStatus?.subscriptionStatus || 'unknown';
  const msg = status === 'canceled' ? 'Your subscription has been canceled.' :
              status === 'past_due' ? 'Payment failed.' :
              status === 'unpaid' ? 'Payment pending.' :
              'Your subscription is no longer active.';
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#F8FAFC', padding:'20px' }}>
      <div style={{ maxWidth:500, background:'#fff', padding:40, borderRadius:12, textAlign:'center', boxShadow:'0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize:24, fontWeight:700, marginBottom:12, color:'#0F172A' }}>🔒 Subscription Needed</div>
        <div style={{ fontSize:14, color:'#64748B', marginBottom:20, lineHeight:1.6 }}>
          {msg} Reactivate your subscription in Settings → Billing to continue using PromoMerge.
        </div>
        <button onClick={() => window.location.hash = '#settings'}
          style={{ padding:'12px 24px', background:BRAND.colors.primary, color:'#fff', border:'none', borderRadius:8, fontWeight:600, cursor:'pointer', fontSize:14, fontFamily:'inherit' }}>
          Go to Settings
        </button>
        <div style={{ marginTop:20, fontSize:12, color:'#94A3B8' }}>
          {dist?.company}
        </div>
      </div>
    </div>
  );
}

function PickPlanPage({ getToken, onAccountCreated }) {
  const [submitting, setSubmitting] = useState(false);
  const [checked, setChecked] = useState(false);
  // Which plan card is currently selected. Previously there was no such
  // state at all — each card's own button checked out immediately on click
  // (skipping the seat picker and the ToS gate entirely), while the bottom
  // "Accept & Continue" button always hardcoded 'starter' regardless of
  // what the person had clicked above. Both bugs came from the same missing
  // piece: nothing tracked which plan was actually chosen.
  const [selectedPlan, setSelectedPlan] = useState('starter');
  // Extra seats beyond the plan's included count, added as a second Stripe
  // line item at signup (server already supports this via extra_seats —
  // see /api/billing/checkout — this was just missing its UI). Clamped to
  // 0-50 to match the same range BillingPage's post-signup seat control
  // enforces server-side.
  const [extraSeats, setExtraSeats] = useState(0);
  // This page only ever renders BEFORE a distributor account exists (see the
  // `if (!dist) return <PickPlanPage .../>` gate in the parent App
  // component), so there is no `dist` object to reference here — it never
  // existed in this component's scope. Use the Clerk session instead, which
  // genuinely is available at this point and gives the person something real
  // to confirm ("yes, this is me") rather than a value that could only ever
  // be undefined.
  const { user } = useUser();

  const PLANS = [
    { name:'Starter', price:'$59', description:'Perfect for getting started', features:['1 team seat','100 AI bundles/mo','Full quote workflow','All integrations'], key:'starter' },
    { name:'Growth', price:'$79', description:'For growing teams', features:['3 team seats','500 AI bundles/mo','Full quote workflow','All integrations'], key:'growth', highlight:true },
    { name:'Enterprise', price:'$99', description:'For large teams', features:['5 team seats','1,000 AI bundles/mo','Full quote workflow','All integrations','Priority support'], key:'enterprise' },
  ];
  const currentPlan = PLANS.find(p => p.key === selectedPlan) || PLANS[0];

  const handleChoosePlan = async () => {
    if (!checked) { alert('Please accept the terms'); return; }
    setSubmitting(true);
    try {
      const token = await getToken();
      const res = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          plan: selectedPlan,
          extra_seats: extraSeats,
          email: user?.primaryEmailAddress?.emailAddress,
        })
      });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Checkout failed'); return; }
      if (data.url) window.location.href = data.url;
    } catch(e) {
      alert('Error: '+e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', background:'#F8FAFC', padding:20 }}>
      <div style={{ textAlign:'center', marginBottom:40, marginTop:40 }}>
        <div style={{ fontSize:32, fontWeight:700, color:BRAND.colors.dark, letterSpacing:'-1px' }}>{BRAND.name}</div>
        <div style={{ fontSize:14, color:BRAND.colors.primary, marginTop:8, fontWeight:500 }}>{BRAND.tagline}</div>
      </div>

      <div style={{ maxWidth:900, width:'100%', margin:'0 auto', marginBottom:40 }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:24 }}>
          {PLANS.map(plan => {
            const isSelected = plan.key === selectedPlan;
            return (
            <div key={plan.key} onClick={() => setSelectedPlan(plan.key)} style={{
              background:'#fff', padding:28, borderRadius:12, cursor:'pointer',
              border: isSelected ? `2px solid ${BRAND.colors.primary}` : '1px solid #E2E8F0',
              boxShadow: isSelected ? `0 0 0 1px ${BRAND.colors.primary}` : 'none',
              position:'relative'
            }}>
              {plan.highlight && (
                <div style={{ position:'absolute', top:-12, right:20, background:BRAND.colors.primary, color:'#fff', padding:'4px 12px', borderRadius:20, fontSize:11, fontWeight:600 }}>
                  POPULAR
                </div>
              )}
              <div style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>{plan.name}</div>
              <div style={{ fontSize:12, color:'#64748B', marginBottom:16 }}>{plan.description}</div>
              <div style={{ fontSize:28, fontWeight:700, marginBottom:4 }}>{plan.price}<span style={{ fontSize:13, color:'#64748B', fontWeight:400 }}>/mo</span></div>
              <div style={{ fontSize:11, color:'#94A3B8', marginBottom:20 }}>7-day free trial · Cancel anytime</div>
              <button onClick={(e) => { e.stopPropagation(); setSelectedPlan(plan.key); }}
                style={{
                  width:'100%', padding:'12px',
                  background: isSelected ? BRAND.colors.primary : '#F8FAFC',
                  color: isSelected ? '#fff' : '#0F172A',
                  border: isSelected ? 'none' : '1px solid #E2E8F0',
                  borderRadius:8, fontWeight:600, cursor:'pointer', fontFamily:'inherit', fontSize:13,
                }}>
                {isSelected ? 'Selected ✓' : 'Select'}
              </button>
              <div style={{ marginTop:16, fontSize:12, color:'#64748B' }}>
                {plan.features.map((f, i) => (
                  <div key={i} style={{ marginBottom:6, display:'flex', gap:8, alignItems:'center' }}>
                    <span style={{ color:BRAND.colors.primary, fontWeight:700 }}>✓</span> {f}
                  </div>
                ))}
              </div>
            </div>
            );
          })}
        </div>
      </div>

      <div style={{ maxWidth:600, width:'100%', margin:'0 auto', background:'#fff', padding:24, borderRadius:12, marginBottom:40 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, paddingBottom:20, borderBottom:'1px solid #E2E8F0' }}>
          <div>
            <div style={{ fontSize:13, fontWeight:600, color:'#0F172A' }}>Need more seats?</div>
            <div style={{ fontSize:11, color:'#94A3B8', marginTop:2 }}>
              {currentPlan.name} includes {currentPlan.features[0]}. Add more below — you can also adjust this anytime after signup in Settings → Billing.
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:12, flexShrink:0, marginLeft:16 }}>
            <button onClick={() => setExtraSeats(s => Math.max(0, s - 1))} disabled={extraSeats === 0}
              style={{
                width:32, height:32, borderRadius:8, border:'1px solid #E2E8F0', background:'#F8FAFC',
                fontSize:16, fontWeight:600, cursor: extraSeats === 0 ? 'not-allowed' : 'pointer',
                opacity: extraSeats === 0 ? 0.4 : 1, fontFamily:'inherit',
              }}>−</button>
            <span style={{ fontSize:15, fontWeight:600, minWidth:20, textAlign:'center' }}>{extraSeats}</span>
            <button onClick={() => setExtraSeats(s => Math.min(50, s + 1))} disabled={extraSeats >= 50}
              style={{
                width:32, height:32, borderRadius:8, border:'1px solid #E2E8F0', background:'#F8FAFC',
                fontSize:16, fontWeight:600, cursor: extraSeats >= 50 ? 'not-allowed' : 'pointer',
                opacity: extraSeats >= 50 ? 0.4 : 1, fontFamily:'inherit',
              }}>+</button>
          </div>
        </div>

        <div style={{ display:'flex', gap:12, marginBottom:12 }}>
          <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)}
            style={{ cursor:'pointer', marginTop:2, flexShrink:0 }} />
          <label style={{ fontSize:13, color:'#475569', cursor:'pointer', lineHeight:1.5 }}>
            I agree to the <a href="#" style={{ color:BRAND.colors.primary, textDecoration:'underline' }}>Terms of Service</a> and <a href="#" style={{ color:BRAND.colors.primary, textDecoration:'underline' }}>Privacy Policy</a>. I understand that PromoMerge is in beta and may change.
          </label>
        </div>
        <button onClick={handleChoosePlan} disabled={!checked || submitting}
          style={{
            cursor: (!checked || submitting) ? 'not-allowed' : 'pointer',
            opacity: (!checked || submitting) ? 0.5 : 1, fontFamily: 'inherit',
          }}>
          {submitting ? 'Saving...' : `Accept & Continue with ${currentPlan.name}${extraSeats > 0 ? ` + ${extraSeats} seat${extraSeats > 1 ? 's' : ''}` : ''} →`}
        </button>

        <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 12, textAlign: 'center' }}>
          Signed in as {user?.primaryEmailAddress?.emailAddress || 'your account'}
        </div>
      </div>
    </div>
  );
}

function AuthScreen() {
  const [mode, setMode] = useState('sign-in');
  // Track viewport width for responsive layout. Inline styles can't use media
  // queries, so we manually swap layouts at the 768px breakpoint.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const toggleLink = (
    <div style={{ fontSize:12, color:'#94A3B8', cursor:'pointer', padding:'12px 0' }}
      onClick={() => setMode(m => m==='sign-in'?'sign-up':'sign-in')}>
      {mode==='sign-in'?"Don't have an account? ":"Already have an account? "}
      <span style={{ color:BRAND.colors.primary }}>{mode==='sign-in'?'Sign up':'Sign in'}</span>
    </div>
  );

  // ── Mobile layout: compact dark header, full-width sign-in below.
  // The 4-feature pitch is dropped on mobile — vertical space is precious and
  // the sign-in form is the primary action.
  if (isMobile) {
    return (
      <div style={{ minHeight:'100vh', background:'#F8FAFC', display:'flex', flexDirection:'column' }}>
        <div style={{ background:BRAND.colors.dark, padding:'20px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:20, fontWeight:700, color:'#fff', letterSpacing:'-0.5px' }}>{BRAND.name}</div>
            <div style={{ fontSize:11, color:BRAND.colors.primary, marginTop:2, fontWeight:500 }}>{BRAND.tagline}</div>
          </div>
          <div style={{ textAlign:'right', flexShrink:0 }}>
            <div style={{ fontSize:16, fontWeight:700, color:'#fff', letterSpacing:'-0.5px' }}>
              From ${BRAND.pricing.startingAt}<span style={{ fontSize:11, fontWeight:400, color:'#94A3B8' }}>/mo</span>
            </div>
          </div>
        </div>
        <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-start', padding:'24px 16px', gap:12 }}>
          {mode === 'sign-in'
            ? <SignIn afterSignInUrl="/#dashboard" routing="hash" />
            : <SignUp afterSignUpUrl="/#dashboard" routing="hash" />
          }
          {toggleLink}
        </div>
      </div>
    );
  }

  // ── Desktop layout: original side-by-side, unchanged.
  return (
    <div style={{ display:'flex', height:'100vh', overflow:'hidden' }}>
      <div style={{ width:420, background:BRAND.colors.dark, padding:'48px 40px', display:'flex', flexDirection:'column', flexShrink:0 }}>
        <div style={{ fontSize:22, fontWeight:700, color:'#fff', letterSpacing:'-0.5px' }}>{BRAND.name}</div>
        <div style={{ fontSize:12, color:BRAND.colors.primary, marginTop:4, fontWeight:500 }}>{BRAND.tagline}</div>
        <div style={{ marginTop:36, flex:1 }}>
          {[
            ['🔍','AI-powered search',  'Natural language — no more 12-field forms'],
            ['📋','Beautiful proposals', 'Branded presentations in under 2 minutes'],
            ['🌐','Your own storefront','Branded client-facing site included'],
            ['📦','PO workflow built-in','Generate supplier POs straight from approved presentations'],
          ].map(([icon,title,sub]) => (
            <div key={title} style={{ display:'flex', gap:12, marginBottom:18 }}>
              <div style={{ width:32, height:32, borderRadius:8, background:`${BRAND.colors.primary}22`,
                display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:16 }}>{icon}</div>
              <div>
                <div style={{ fontSize:13, fontWeight:500, color:'#E2E8F0' }}>{title}</div>
                <div style={{ fontSize:12, color:'#64748B', marginTop:2 }}>{sub}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ background:`${BRAND.colors.primary}18`, border:`1px solid ${BRAND.colors.primary}33`, borderRadius:10, padding:16 }}>
          <div style={{ fontSize:26, fontWeight:700, color:'#fff', letterSpacing:'-1px' }}>
            Starting at ${BRAND.pricing.startingAt}<span style={{ fontSize:14, fontWeight:400, color:'#94A3B8' }}>/month</span>
          </div>
          {/* "All features included" was removed — it stopped being true once
              plans started gating AI bundle quota and team seats. */}
          <div style={{ fontSize:12, color:'#94A3B8', marginTop:6, lineHeight:1.5 }}>
            Your one-stop shop for running a promo business · Cancel anytime
          </div>
        </div>
      </div>
      <div style={{ flex:1, background:'#F8FAFC', display:'flex', alignItems:'center', justifyContent:'center', padding:40, flexDirection:'column', gap:16 }}>
        {mode === 'sign-in'
          ? <SignIn afterSignInUrl="/#dashboard" routing="hash" />
          : <SignUp afterSignUpUrl="/#dashboard" routing="hash" />
        }
        {toggleLink}
      </div>
    </div>
  );
}

function AuthGate() {
  const { isLoaded, isSignedIn } = useAuth();
  if (!isLoaded) return <Spinner />;
  if (!isSignedIn) return <AuthScreen />;
  return <AppShell />;
}

export default function App() {
  // Public storefront route — /store/:slug renders the customer-facing page
  // without going through Clerk auth. Anyone can view a distributor's storefront.
  // Collapse duplicate slashes before matching. Links are built as
  // `${APP_URL}/accept-invite/<token>` and `${origin}/p/<token>`; a trailing
  // slash on APP_URL produces '//accept-invite/...', which failed every route
  // and dumped the invitee on the signed-out marketing shell with no
  // explanation. Normalizing here fixes it for every route at once rather
  // than relying on an env var being punctuated correctly.
  const routePath = window.location.pathname.replace(/\/{2,}/g, '/');

  // Public terms of service page — /terms
  if (routePath === '/terms' || routePath === '/terms/') {
    return <TermsOfServicePage />;
  }
  const storeMatch = routePath.match(/^\/store\/([a-z0-9-]+)\/?$/);
  if (storeMatch) {
    return <PublicStorefront slug={storeMatch[1]} />;
  }
  // Public presentation route — /p/:token renders a curated proposal for a
  // specific client. The token is the auth (whoever has the URL can view).
  const presMatch = routePath.match(/^\/p\/([a-zA-Z0-9]+)\/?$/);
  if (presMatch) {
    return <PublicPresentation token={presMatch[1]} />;
  }
  // Public invoice route — /invoices/:shareToken renders a public payment page
  const invMatch = routePath.match(/^\/invoices\/([a-zA-Z0-9]{24,})\/?$/);
  if (invMatch) {
    return <PublicInvoice shareToken={invMatch[1]} />;
  }
  // Accept invite route — /accept-invite/:token onboards an invited team
  // member. AcceptInvite handles its own auth (sign-up vs sign-in) since
  // the invitee may not have a Clerk account yet.
  // NOTE the trailing (?:\/.*)? — Clerk's <SignUp routing="path"> owns sub-paths
  // under this URL: /verify-email-address, /continue, /sso-callback, etc. An
  // anchored $ here meant the route stopped matching the instant Clerk moved
  // to email verification, dropping the invitee onto the generic signed-out
  // shell mid-signup — where their half-created account naturally produced
  // "Couldn't find your account". The token is still captured from group 1.
  const inviteMatch = routePath.match(/^\/accept-invite\/([a-fA-F0-9]+)(?:\/.*)?$/i);
  if (inviteMatch) {
    return (
      <ClerkProvider publishableKey={CLERK_KEY} appearance={{
        variables: { colorPrimary: BRAND.colors.primary, fontFamily:"'DM Sans',system-ui,sans-serif" },
        elements: { card:{ boxShadow:'none', border:'1px solid #E2E8F0' } }
      }}>
        <AcceptInvite token={inviteMatch[1]} />
      </ClerkProvider>
    );
  }

  return (
    <ClerkProvider publishableKey={CLERK_KEY} appearance={{
      variables: { colorPrimary: BRAND.colors.primary, fontFamily:"'DM Sans',system-ui,sans-serif" },
      elements: { card:{ boxShadow:'none', border:'1px solid #E2E8F0' } }
    }}>
      <AuthGate />
    </ClerkProvider>
  );
}

// Import PublicInvoice for the public route
function PublicInvoice({ shareToken }) {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canPay, setCanPay] = useState(true);
  const [payingError, setPayingError] = useState('');

  useEffect(() => {
    const loadInvoice = async () => {
      try {
        // If Stripe just redirected back with a session id, verify the payment
        // first (webhook-free confirmation) so the page shows Paid immediately.
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get('session_id');
        if (sessionId) {
          try {
            const cRes = await fetch(`/api/invoices/confirm/${shareToken}?session_id=${encodeURIComponent(sessionId)}`);
            const cData = await cRes.json();
            if (cData.ok && cData.paid && cData.invoice) {
              setInvoice(cData.invoice);
              setLoading(false);
              // Strip the query string so a refresh doesn't re-run confirmation.
              window.history.replaceState({}, '', `/invoices/${shareToken}`);
              return;
            }
          } catch (e) {
            // Fall through to a normal load if confirmation hiccups.
          }
        }

        const res = await fetch(`/api/p/invoices/${shareToken}`);
        if (!res.ok) {
          setError(res.status === 404 ? 'Invoice not found or expired' : 'Failed to load invoice');
          setLoading(false);
          return;
        }
        const data = await res.json();
        if (data.ok && data.invoice) {
          setInvoice(data.invoice);
          setCanPay(data.canAcceptPayment !== false);
        } else {
          setError('Failed to load invoice');
        }
      } catch (err) {
        console.error('Error loading invoice:', err);
        setError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadInvoice();
  }, [shareToken]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8FAFC' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 14, color: '#666' }}>Loading invoice...</div>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8FAFC', padding: '20px' }}>
        <div style={{ maxWidth: '500px', background: '#FFF', padding: '40px', borderRadius: '12px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: 18, color: '#EF4444', marginBottom: '16px', fontWeight: 600 }}>⚠ Unable to Load Invoice</div>
          <div style={{ color: '#666', marginBottom: '24px', lineHeight: '1.6' }}>
            {error || 'Invoice not found.'}
          </div>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              background: '#000',
              color: '#FFF',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
              fontFamily: 'inherit',
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Full itemized invoice — replaces the earlier placeholder that showed
  // only invoice number, status, and total. Line items come from the
  // invoice's own stored `line_items` (set once at creation, authoritative —
  // never recalculated here), matching the exact field names CreateInvoiceModal
  // writes: description, qty, unit_price, subtotal.
  const lineItems = Array.isArray(invoice.line_items) ? invoice.line_items : [];
  const money = (n) => `$${Number(n || 0).toFixed(2)}`;

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', padding: '40px 20px' }}>
      <div style={{ maxWidth: '640px', margin: '0 auto', background: '#FFF', borderRadius: '12px', padding: '40px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 28, fontWeight: 600, marginBottom: 4 }}>Invoice</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#475569' }}>#{invoice.invoice_number}</div>
          </div>
          <div style={{
            padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600,
            background: invoice.payment_status === 'paid' ? '#D1FAE5' : '#FEF3C7',
            color: invoice.payment_status === 'paid' ? '#065F46' : '#92400E',
          }}>
            {invoice.payment_status === 'paid' ? '✓ Paid' : 'Unpaid'}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 32, paddingBottom: 24, borderBottom: '1px solid #E2E8F0', fontSize: 13, color: '#64748B' }}>
          <div>
            <div style={{ fontWeight: 600, color: '#0F172A', marginBottom: 4 }}>From</div>
            <div>{invoice.distributor_company || 'Your supplier'}</div>
            {invoice.distributor_contact_email && <div>{invoice.distributor_contact_email}</div>}
            {invoice.distributor_contact_phone && <div>{invoice.distributor_contact_phone}</div>}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 600, color: '#0F172A', marginBottom: 4 }}>Billed to</div>
            <div>{invoice.client_name || '—'}</div>
            {invoice.client_email && <div>{invoice.client_email}</div>}
            {invoice.due_date && <div style={{ marginTop: 8 }}>Due {new Date(invoice.due_date).toLocaleDateString()}</div>}
          </div>
        </div>

        {/* Ship to — present when the invoice carries an address (filled at
            creation from the client's default saved address, or edited in
            the Create Invoice form). Its own block: billing contact and
            delivery address often differ. */}
        {invoice.ship_to && (invoice.ship_to.address1 || invoice.ship_to.company || invoice.ship_to.name) && (
          <div style={{ marginBottom: 32, paddingBottom: 24, borderBottom: '1px solid #E2E8F0', fontSize: 13, color: '#64748B' }}>
            <div style={{ fontWeight: 600, color: '#0F172A', marginBottom: 4 }}>Ship to</div>
            {invoice.ship_to.name && <div>{invoice.ship_to.name}</div>}
            {invoice.ship_to.company && <div>{invoice.ship_to.company}</div>}
            {invoice.ship_to.address1 && <div>{invoice.ship_to.address1}</div>}
            {invoice.ship_to.address2 && <div>{invoice.ship_to.address2}</div>}
            {(invoice.ship_to.city || invoice.ship_to.state || invoice.ship_to.zip) && (
              <div>{[invoice.ship_to.city, invoice.ship_to.state].filter(Boolean).join(', ')} {invoice.ship_to.zip || ''}</div>
            )}
            {invoice.ship_to.phone && <div>{invoice.ship_to.phone}</div>}
          </div>
        )}

        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 90px 90px', gap: 8, fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', paddingBottom: 8, borderBottom: '1px solid #E2E8F0' }}>
            <div>Description</div>
            <div style={{ textAlign: 'right' }}>Qty</div>
            <div style={{ textAlign: 'right' }}>Price</div>
            <div style={{ textAlign: 'right' }}>Amount</div>
          </div>
          {lineItems.map((item, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 90px 90px', gap: 8, fontSize: 14, padding: '12px 0', borderBottom: '1px solid #F1F5F9' }}>
              <div style={{ color: '#0F172A' }}>{item.description || 'Item'}</div>
              <div style={{ textAlign: 'right', color: '#64748B' }}>{item.qty}</div>
              <div style={{ textAlign: 'right', color: '#64748B' }}>{money(item.unit_price)}</div>
              <div style={{ textAlign: 'right', color: '#0F172A', fontWeight: 500 }}>{money(item.subtotal ?? (item.qty * item.unit_price))}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, marginBottom: 24, fontSize: 14 }}>
          <div style={{ display: 'flex', gap: 40 }}>
            <span style={{ color: '#64748B' }}>Subtotal</span>
            <span style={{ minWidth: 80, textAlign: 'right' }}>{money(invoice.subtotal)}</span>
          </div>
          {Number(invoice.tax_amount) > 0 && (
            <div style={{ display: 'flex', gap: 40 }}>
              <span style={{ color: '#64748B' }}>{invoice.tax_label || 'Tax'} {invoice.tax_rate ? `(${invoice.tax_rate}%)` : ''}</span>
              <span style={{ minWidth: 80, textAlign: 'right' }}>{money(invoice.tax_amount)}</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 40, fontSize: 18, fontWeight: 700, paddingTop: 8, borderTop: '2px solid #0F172A', marginTop: 4 }}>
            <span>Total</span>
            <span style={{ minWidth: 80, textAlign: 'right' }}>{money(invoice.total)}</span>
          </div>
        </div>

        {invoice.notes && (
          <div style={{ fontSize: 13, color: '#64748B', marginBottom: 24, padding: 12, background: '#F8FAFC', borderRadius: 8 }}>
            {invoice.notes}
          </div>
        )}

        {payingError && (
          <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', color:'#991B1B', borderRadius:8, padding:'10px 12px', fontSize:13, marginBottom:12 }}>
            {payingError}
          </div>
        )}
        {invoice.payment_status !== 'paid' && !canPay && (
          <div style={{ background:'#FEF3C7', border:'1px solid #FDE68A', color:'#92400E', borderRadius:8, padding:'10px 12px', fontSize:13, marginBottom:12 }}>
            This business hasn't finished setting up online payments yet. Please contact them to arrange payment.
          </div>
        )}
        {invoice.payment_status !== 'paid' && canPay && (
          <button
            onClick={async () => {
              setPayingError('');
              try {
                const res = await fetch(`/api/invoices/pay/${shareToken}`, { method: 'POST' });
                const data = await res.json();
                if (res.ok && data.url) {
                  window.location.href = data.url;
                } else {
                  setPayingError(data.error || 'Could not start checkout. Please try again.');
                }
              } catch (err) {
                setPayingError('Network error. Please try again.');
              }
            }}
            style={{
              width: '100%',
              padding: '12px',
              background: '#000',
              color: '#FFF',
              border: 'none',
              borderRadius: '8px',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Pay {money(invoice.total)}
          </button>
        )}
      </div>
    </div>
  );
}
